import React, { useCallback, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import styles from './UIGameFeedVirtualScrollContainer.module.scss';
import { useGameStore } from '../../../store/GameStoreContext.ts';
const SCROLL_BOTTOM_THRESHOLD = 50;
export function UIGameFeedVirtualScrollContainer({ children, scrollToChildIndex, autoScrollToBottom, suppressAutoScrollOnUserScroll = false }) {
    const gameOrientation = useGameStore((state)=>state.gameClientConfig.gameOrientation);
    const isMouseOverRef = useRef(false);
    const userHasScrolledUpRef = useRef(false);
    const isAutoScrollingRef = useRef(false);
    const parentRef = useRef(null);
    const previousScrollToIndexRef = useRef(-1);
    const lastScrollTimeRef = useRef(0);
    const handleScroll = useCallback(()=>{
        if (!suppressAutoScrollOnUserScroll) return;
        const isPortrait = gameOrientation == 'portrait';
        if (!isPortrait) return;
        if (isAutoScrollingRef.current) return;
        const scrollElement = parentRef.current;
        if (scrollElement == null) return;
        const distanceFromBottom = scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
        const isAtBottom = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD;
        userHasScrolledUpRef.current = !isAtBottom;
    }, [
        suppressAutoScrollOnUserScroll,
        gameOrientation
    ]);
    const virtualizer = useVirtualizer({
        count: children.length,
        getScrollElement: ()=>parentRef.current,
        estimateSize: ()=>50
    });
    useEffect(()=>{
        if (scrollToChildIndex != null && scrollToChildIndex >= 0) {
            const currentTime = Date.now();
            const timeSinceLastScroll = currentTime - lastScrollTimeRef.current;
            const previousScrollToIndex = previousScrollToIndexRef.current;
            const jumpDistance = Math.abs(scrollToChildIndex - previousScrollToIndex);
            const scrollDuration = 500;
            const distanceToSmoothScroll = 5;
            const shouldUseInstantScroll = timeSinceLastScroll < scrollDuration || jumpDistance > distanceToSmoothScroll;
            if (shouldUseInstantScroll) {
                virtualizer.scrollToIndex(scrollToChildIndex, {
                    align: 'center',
                    behavior: 'auto'
                });
            } else {
                virtualizer.scrollToIndex(scrollToChildIndex, {
                    align: 'center',
                    behavior: 'smooth'
                });
            }
            lastScrollTimeRef.current = currentTime;
            previousScrollToIndexRef.current = scrollToChildIndex;
        }
    }, [
        scrollToChildIndex
    ]);
    useEffect(()=>{
        if (isMouseOverRef.current) return;
        if (userHasScrolledUpRef.current) return;
        if (autoScrollToBottom && scrollToChildIndex === undefined && children.length > 0) {
            isAutoScrollingRef.current = true;
            virtualizer.scrollToIndex(children.length - 1, {
                align: 'end',
                behavior: 'auto'
            });
            requestAnimationFrame(()=>{
                isAutoScrollingRef.current = false;
            });
        }
    }, [
        children,
        scrollToChildIndex,
        virtualizer,
        autoScrollToBottom
    ]);
    const items = virtualizer.getVirtualItems();
    return /*#__PURE__*/ React.createElement("div", {
        onMouseEnter: ()=>isMouseOverRef.current = true,
        onMouseLeave: ()=>isMouseOverRef.current = false,
        onScroll: handleScroll,
        ref: parentRef,
        className: styles.virtualContainer
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.virtualScroller,
        style: {
            height: `${virtualizer.getTotalSize()}px`
        }
    }, items.map((virtualItem)=>{
        const child = children[virtualItem.index];
        if (child == null) return null;
        return /*#__PURE__*/ React.createElement("div", {
            key: virtualItem.key,
            "data-index": virtualItem.index,
            ref: virtualizer.measureElement,
            className: styles.scrollItemContainer,
            style: {
                transform: `translateY(${virtualItem.start}px)`
            }
        }, child);
    })));
}
